import * as React from "react";
import {
  Box,
  Flex,
  InputGroup,
  InputLeftAddon,
  Select,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  useGetCompanyDimensionReports,
  useGetDimensionsReport,
  useGetReportId
} from "../../../../../hooks/query/reports";
import { useState } from "react";
import { QuarterSelector } from "../../../../../components/QuarterSelector";
import { showErrorToast } from "../../../../../utils";
import { YearSelector } from "../../../../../components";
import { hideAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import { useGetOrganizationStructure } from "../../../../../hooks/query";
import { ByDimensionChart, ConsumptionsByDimensionModal } from "./components";
import { mapByDimensionReportData } from "../helpers";
import { Paginator } from "../../../../../components/Paginator";

function ByDimensionTab({ selectedYear, setSelectedYear, selectedQuarter, setSelectedQuarter }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "companyEmissionsDashboard"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const toast = useToast();

  const {
    isOpen: isConsumptionsByDimensionModalOpen,
    onOpen: onConsumptionsByDimensionModalOpen,
    onClose: onConsumptionsByDimensionModalClose
  } = useDisclosure();

  const [chartData, setChartData] = useState([]);
  const [paginatorObj, setPaginatorObj] = useState({
    queryPageIndex: 0,
    totalPages: 0
  });
  const queryPageSize = 10;

  const handlePageSelection = (newPage) => {
    setChartData([]);
    setPaginatorObj({
      ...paginatorObj,
      queryPageIndex: newPage - 1
    });
  };

  const {
    data: organizationStructure
  } = useGetOrganizationStructure({
    onSuccess: (data) => {
      if (typeof selectedDimension === "undefined" || !selectedDimension) {
        setSelectedDimension(data.dimensions?.dimensionOne?.id ?? null);
      }
    },
    refetchOnWindowFocus: false
  });

  const [selectedDimension, setSelectedDimension] = useState(
    organizationStructure?.dimensions?.dimensionOne?.id ?? null);

  const selectorFlow = useBreakpointValue({
    sm: "row",
    base: "column"
  });

  const {
    isFetching: isChartDataFetching
  } = useGetCompanyDimensionReports(selectedDimension, {
    year: selectedYear, quarter: selectedQuarter,
    page: paginatorObj.queryPageIndex, size: queryPageSize
  }, {
    enabled: selectedDimension !== null && typeof selectedDimension !== "undefined",
    onSuccess: (data) => {
      if (data) {
        if (data.content?.length > 0) {
          const mappedData = mapByDimensionReportData(data);
          setChartData(mappedData);
        } else {
          setChartData([]);
        }
        setPaginatorObj({
          ...paginatorObj,
          totalPages: data.totalPages
        });
      }
    },
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const handleErrors = (error) => {
    if (error.response?.status === 404) {
      return;
    }
    let errorMessage = error.response?.data?.parameters?.message ?? error.message;
    showErrorToast(toast, errorMessage);
  };

  const {
    data: reportId
  } = useGetReportId({
    year: selectedYear,
    quarter: selectedQuarter
  }, {
    onError: err => handleErrors(err),
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const [clickedBar, setClickedBar] = useState(null);

  const getDimensionParams = () => {
    if (organizationStructure && typeof organizationStructure !== "undefined") {
      const foundDimensionKey = Object.keys(organizationStructure.dimensions)?.find(key => {
        return organizationStructure.dimensions[key]?.id === selectedDimension;
      });

      if (typeof foundDimensionKey !== "undefined") {
        const foundDimItem = organizationStructure.dimensions?.[foundDimensionKey]?.items?.find(item =>
          item.name === clickedBar
        );
        if (typeof foundDimItem !== "undefined") {
          return `${foundDimensionKey}=${foundDimItem.id}`;
        }
      }
    }

    return "";
  };

  const {
    data: dimensionsReportData,
    isFetching: isDimensionsReportDataFetching
  } = useGetDimensionsReport(reportId, getDimensionParams(),
    {
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      enabled: getDimensionParams() !== "" && typeof organizationStructure !== "undefined" &&
        typeof reportId !== "undefined" && clickedBar !== null,
      cacheTime: 0
    }
  );

  const handleBarClick = (el) => {
    onConsumptionsByDimensionModalOpen();
    setClickedBar(el.indexValue);
  };

  const handleConsumptionsByDimensionModalClose = () => {
    onConsumptionsByDimensionModalClose();
    setClickedBar(null);
  };

  return (
    <Box>
      {organizationStructure ?
        <Box>
          <InputGroup display="flex" alignItems="center" width="fit-content" marginBlock={4}>
            <InputLeftAddon children={t("byDimension.dimension")} fontWeight="semibold" />
            <Select
              backgroundColor="white"
              borderRadius="none"
              value={selectedDimension}
              onChange={(e) => {
                setChartData([]);
                setPaginatorObj({
                  queryPageIndex: 0,
                  totalPages: 0
                });
                setSelectedDimension(parseInt(e.target.value));
              }}
            >
              {Object.keys(organizationStructure.dimensions)
                .filter(key => organizationStructure.dimensions[key] !== null)
                .sort((a, b) => {
                  return (organizationStructure.dimensions[a].id > organizationStructure.dimensions[b].id) ? 1 :
                    ((organizationStructure.dimensions[b].id > organizationStructure.dimensions[a].id) ? -1 : 0);
                })
                .map(dimKey => {
                  return <option
                    key={`dim-key-${organizationStructure.dimensions[dimKey].id}`}
                    value={organizationStructure.dimensions[dimKey].id}
                  >
                    {organizationStructure.dimensions[dimKey].name}
                  </option>;
                })}
            </Select>
          </InputGroup>
          <Flex gap={selectorFlow === "row" ? 4 : 0} flexFlow={selectorFlow}>
            <YearSelector selectedYear={selectedYear} handleYearSelection={(value) => setSelectedYear(value)} />
            <QuarterSelector
              selectedQuarter={selectedQuarter}
              handleQuarterSelection={(value) => setSelectedQuarter(value)}
            />
          </Flex>
        </Box> : null
      }
      {paginatorObj && paginatorObj.totalPages !== 0 ?
        <Paginator
          selectedPage={paginatorObj.queryPageIndex + 1}
          numberOfPages={paginatorObj.totalPages}
          handlePageSelection={(newPage) => handlePageSelection(newPage)}
        /> : null
      }
      {!isChartDataFetching && organizationStructure?.dimensions?.dimensionOne !== null && chartData && chartData.length > 0 ?
        <ByDimensionChart data={chartData} handleBarClick={handleBarClick} /> :
        !isChartDataFetching && (!chartData || chartData.length === 0) ?
          <Text fontSize="lg" fontWeight="bold">
            {globalTranslation("noDataAvailable")}
          </Text> : null
      }
      {isConsumptionsByDimensionModalOpen && !isDimensionsReportDataFetching && (
        <ConsumptionsByDimensionModal
          modalTitle={`${clickedBar} - ${selectedYear} ${selectedQuarter}`}
          dimensionData={dimensionsReportData}
          isOpen={isConsumptionsByDimensionModalOpen}
          onClose={handleConsumptionsByDimensionModalClose}
        />)
      }
    </Box>
  );
}

export default ByDimensionTab;